import {
  Component,
  Inject,
  InjectionToken,
  Injector,
  Optional,
  ChangeDetectionStrategy,
  inject,
  viewChild,
  input,
  output,
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormControlDirective,
  UntypedFormControl,
} from '@angular/forms';
import { BaseFormControlDirective } from './directives/base-form-control.directive';
import { uniqueId } from '@shared/utils';

export interface ControlValueAccessorConfig {
  change: boolean;
}
export const CONTROL_VALUE_ACCESSOR_CONFIG = new InjectionToken<Partial<ControlValueAccessorConfig>>(
  'controlValueAccessorConnector'
);

@Component({
  template: ``,
  changeDetection: ChangeDetectionStrategy.Eager,
})
// tslint:disable-next-line: component-class-suffix
export class ControlValueAccessorConnectorComponent implements ControlValueAccessor {
  readonly formControlDirective = viewChild.required(FormControlDirective);

  readonly formControl = input<FormControl<unknown> | FormArray<never>>();

  readonly formControlName = input<string>();

  configSetup = {
    change: true,
    ...this.config,
  };

  constructor(
    private injector: Injector,
    @Optional() @Inject(CONTROL_VALUE_ACCESSOR_CONFIG) protected config?: Partial<ControlValueAccessorConfig>
  ) {}

  get control() {
    return this.formControl()! || this.controlContainer?.control?.get(this.formControlName()!);
  }

  get getFormControl() {
    return this.control as FormControl<unknown>;
  }

  get controlContainer() {
    return this.injector.get(ControlContainer);
  }

  // implements ControlValueAccessor
  registerOnTouched(fn: unknown): void {
    this.formControlDirective().valueAccessor?.registerOnTouched(fn);
  }

  // implements ControlValueAccessor
  registerOnChange(fn: unknown): void {
    if (this.configSetup?.change) this.formControlDirective().valueAccessor?.registerOnChange(fn);
  }

  // implements ControlValueAccessor
  writeValue(obj: unknown): void {
    this.formControlDirective().valueAccessor?.writeValue(obj);
  }

  // implements ControlValueAccessor
  setDisabledState(isDisabled: boolean): void {
    const formControlDirective = this.formControlDirective();
    if (formControlDirective.valueAccessor?.setDisabledState)
      formControlDirective.valueAccessor.setDisabledState(isDisabled);
  }
}

/**
 * Společný základ dvoustavových polí (ui-checkbox, ui-toggle-switch, ui-tri-checkbox): vstupy BaseFormControlDirective,
 * id vstupu pro popisek, výstup handleChange a control z formControl i formControlName.
 * `T` je typ hodnoty v handleChange (ui-tri-checkbox má i null). Komponenta, ne direktiva: direktiva nesmí dědit
 * z komponenty (NG0903).
 */
@Component({
  template: ``,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export abstract class BinaryControlConnectorComponent<
  T extends boolean | null = boolean,
> extends ControlValueAccessorConnectorComponent {
  readonly handleChange = output<T>();

  readonly base = inject(BaseFormControlDirective);

  readonly randomInputId = uniqueId();

  handleChangeWrapper(evt: { checked?: unknown }) {
    this.handleChange.emit(Boolean(evt.checked) as T);
  }

  override get control(): UntypedFormControl {
    return (this.formControl() ||
      this.controlContainer?.control?.get(this.formControlName() ?? '')) as UntypedFormControl;
  }
}
